import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faClock } from "@fortawesome/pro-solid-svg-icons/faClock";
import { faMinusCircle } from "@fortawesome/pro-solid-svg-icons/faMinusCircle";
import { faStar } from "@fortawesome/pro-solid-svg-icons/faStar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import { defineMessages, useIntl } from "react-intl";

import { Button } from "@mapmycustomers/ui";

import {
  removeSearchHistoryItem,
  startLeadFinderSearch,
} from "@app/scene/map/store/leadFinder/actions";
import {
  getLeadFinderRecentSearches,
  getLeadFinderSavedSearches,
} from "@app/scene/map/store/leadFinder/selectors";
import { LEAD_FINDER_MAX_SEARCHES } from "@app/scene/map/utils/consts";
import { isCurrentUserOwner } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import showLeadFinderLimitAlert from "@app/util/leadFinder/showLeadFinderLimitAlert";

import styles from "./History.module.scss";

interface Props {
  isOwner: boolean;
  recentSearches: Array<string>;
  removeSearchHistoryItem: typeof removeSearchHistoryItem;
  savedSearches: Array<string>;
  startLeadFinderSearch: typeof startLeadFinderSearch.request;
}

const messages = defineMessages({
  empty: {
    id: "map.tool.leadFinder.popover.history.empty",
    defaultMessage: "No search history",
    description: "Lead Finder search box no search history title",
  },
  remove: {
    id: "map.tool.leadFinder.popover.history.remove",
    defaultMessage: "Remove",
    description: "Lead Finder popover search item - remove button title",
  },
  title: {
    id: "map.tool.leadFinder.popover.history.title",
    defaultMessage: "Quick Search",
    description: "Lead Finder popover quick search title",
  },
});

const History: React.FC<Props> = ({
  isOwner,
  recentSearches,
  removeSearchHistoryItem,
  savedSearches,
  startLeadFinderSearch,
}) => {
  const intl = useIntl();

  const hasHistory = recentSearches.length + savedSearches.length > 0;
  const recentLimit = Math.max(LEAD_FINDER_MAX_SEARCHES - savedSearches.length, 0);

  const recent = useMemo(() => recentSearches.slice(0, recentLimit), [recentLimit, recentSearches]);
  const saved = useMemo(() => savedSearches.slice(0, LEAD_FINDER_MAX_SEARCHES), [savedSearches]);

  const onSearch = useCallback(
    (value) => {
      startLeadFinderSearch({ search: value });
    },
    [startLeadFinderSearch]
  );

  const handleSearch = useCallback(
    (value: string) => {
      startLeadFinderSearch({
        onLimitThresholdReached: (limits) => {
          showLeadFinderLimitAlert(intl, limits, isOwner, onSearch, value);
        },
        search: value,
      });
    },
    [intl, isOwner, onSearch, startLeadFinderSearch]
  );

  return (
    <div className={styles.container}>
      <div className={styles.title}>
        {hasHistory ? intl.formatMessage(messages.title) : intl.formatMessage(messages.empty)}
      </div>

      {hasHistory && (
        <div className={styles.list}>
          {saved.map((query, index) => (
            <div className={styles.item} key={index}>
              <div className={styles.content}>
                <Button
                  block
                  className={styles.btn}
                  icon={<FontAwesomeIcon icon={faStar} />}
                  onClick={() => handleSearch(query)}
                  size="small"
                  type="text"
                >
                  {query}
                </Button>

                <Tooltip placement="right" title={intl.formatMessage(messages.remove)}>
                  <Button
                    className={styles.removeBtn}
                    icon={<FontAwesomeIcon icon={faMinusCircle} />}
                    iconOnly
                    onClick={() => removeSearchHistoryItem({ search: query })}
                    size="small"
                    type="link"
                  />
                </Tooltip>
              </div>
            </div>
          ))}

          {recent.map((query, index) => (
            <div className={styles.item} key={index}>
              <div className={styles.content}>
                <Button
                  block
                  className={styles.btn}
                  icon={<FontAwesomeIcon icon={faClock} />}
                  onClick={() => handleSearch(query)}
                  size="small"
                  type="text"
                >
                  {query}
                </Button>

                <Tooltip placement="right" title={intl.formatMessage(messages.remove)}>
                  <Button
                    className={styles.removeBtn}
                    icon={<FontAwesomeIcon icon={faMinusCircle} />}
                    iconOnly
                    onClick={() => removeSearchHistoryItem({ search: query })}
                    size="small"
                    type="link"
                  />
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  isOwner: isCurrentUserOwner(state),
  recentSearches: getLeadFinderRecentSearches(state),
  savedSearches: getLeadFinderSavedSearches(state),
});

const mapDispatchToProps = {
  removeSearchHistoryItem: removeSearchHistoryItem,
  startLeadFinderSearch: startLeadFinderSearch.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(History);
